import { Schema } from "effect";

/**
 * The three independently configurable FrontDesk+ kiosk capabilities.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * import { Schema } from "effect";
 * import { KioskPolicyFlags } from "@ascleo/frontdesk-contracts/kiosk-policy/policy";
 *
 * Schema.decodeUnknownSync(KioskPolicyFlags)({
 *   patientCreationEnabled: false,
 *   manualPrescriptionFallbackEnabled: false,
 *   localEmergencyAlertEnabled: true,
 * });
 * ```
 */
export const KioskPolicyFlags = Schema.Struct({
  patientCreationEnabled: Schema.Boolean,
  manualPrescriptionFallbackEnabled: Schema.Boolean,
  localEmergencyAlertEnabled: Schema.Boolean,
});

/**
 * The decoded type of {@link KioskPolicyFlags}.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * import type { KioskPolicyFlags } from "@ascleo/frontdesk-contracts/kiosk-policy/policy";
 *
 * const flags: KioskPolicyFlags = {
 *   patientCreationEnabled: false,
 *   manualPrescriptionFallbackEnabled: false,
 *   localEmergencyAlertEnabled: false,
 * };
 * ```
 */
export type KioskPolicyFlags = typeof KioskPolicyFlags.Type;

/**
 * The fail-closed default used before a practice explicitly opts in.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * import { DEFAULT_KIOSK_POLICY_FLAGS } from "@ascleo/frontdesk-contracts/kiosk-policy/policy";
 *
 * DEFAULT_KIOSK_POLICY_FLAGS.patientCreationEnabled; // false
 * ```
 */
export const DEFAULT_KIOSK_POLICY_FLAGS: KioskPolicyFlags = {
  patientCreationEnabled: false,
  manualPrescriptionFallbackEnabled: false,
  localEmergencyAlertEnabled: false,
};

/**
 * Whether the authoritative practice policy store could be consulted.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * import { Schema } from "effect";
 * import { KioskPolicyAvailability } from "@ascleo/frontdesk-contracts/kiosk-policy/policy";
 *
 * Schema.decodeUnknownSync(KioskPolicyAvailability)("available");
 * ```
 */
export const KioskPolicyAvailability = Schema.Literals(
  ["available",
  "unavailable"],
);

/**
 * The decoded type of {@link KioskPolicyAvailability}.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * import type { KioskPolicyAvailability } from "@ascleo/frontdesk-contracts/kiosk-policy/policy";
 *
 * const availability: KioskPolicyAvailability = "unavailable";
 * ```
 */
export type KioskPolicyAvailability = typeof KioskPolicyAvailability.Type;

/**
 * The practice-wide policy view exposed to administrators and kiosks.
 *
 * Version 0 means the reachable store has no saved policy yet. The contract carries
 * no practice, tenant, actor, or persistence selector because the Bridge deployment
 * owns the practice boundary.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * import { Schema } from "effect";
 * import { KioskPolicyView } from "@ascleo/frontdesk-contracts/kiosk-policy/policy";
 *
 * Schema.decodeUnknownSync(KioskPolicyView)({
 *   flags: {
 *     patientCreationEnabled: false,
 *     manualPrescriptionFallbackEnabled: false,
 *     localEmergencyAlertEnabled: false,
 *   },
 *   version: 0,
 *   availability: "available",
 * });
 * ```
 */
export const KioskPolicyView = Schema.Struct({
  flags: KioskPolicyFlags,
  version: Schema.Int.pipe(Schema.check(Schema.isGreaterThanOrEqualTo(0))),
  availability: KioskPolicyAvailability,
  updatedAt: Schema.optional(Schema.DateTimeUtcFromString),
});

/**
 * The decoded type of {@link KioskPolicyView}.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * import type { KioskPolicyView } from "@ascleo/frontdesk-contracts/kiosk-policy/policy";
 *
 * function isCurrent(view: KioskPolicyView): boolean {
 *   return view.availability === "available";
 * }
 * ```
 */
export type KioskPolicyView = typeof KioskPolicyView.Type;

/**
 * An administrator's optimistic update of all kiosk policy flags.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * import { Schema } from "effect";
 * import { KioskPolicyUpdate } from "@ascleo/frontdesk-contracts/kiosk-policy/policy";
 *
 * Schema.decodeUnknownSync(KioskPolicyUpdate)({
 *   flags: {
 *     patientCreationEnabled: true,
 *     manualPrescriptionFallbackEnabled: false,
 *     localEmergencyAlertEnabled: false,
 *   },
 *   expectedVersion: 0,
 * });
 * ```
 */
export const KioskPolicyUpdate = Schema.Struct({
  flags: KioskPolicyFlags,
  expectedVersion: Schema.Int.pipe(Schema.check(Schema.isGreaterThanOrEqualTo(0))),
});

/**
 * The decoded type of {@link KioskPolicyUpdate}.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * import type { KioskPolicyUpdate } from "@ascleo/frontdesk-contracts/kiosk-policy/policy";
 *
 * const update: KioskPolicyUpdate = {
 *   flags: {
 *     patientCreationEnabled: false,
 *     manualPrescriptionFallbackEnabled: true,
 *     localEmergencyAlertEnabled: false,
 *   },
 *   expectedVersion: 4,
 * };
 * ```
 */
export type KioskPolicyUpdate = typeof KioskPolicyUpdate.Type;

/**
 * A typed result for an optimistic kiosk policy save.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * import { Schema } from "effect";
 * import { KioskPolicySaveResponse } from "@ascleo/frontdesk-contracts/kiosk-policy/policy";
 *
 * Schema.decodeUnknownSync(KioskPolicySaveResponse)({ outcome: "unavailable" });
 * ```
 */
export const KioskPolicySaveResponse = Schema.Union(
  [Schema.Struct({
    outcome: Schema.Literal("saved"),
    view: KioskPolicyView,
  }),
  Schema.Struct({
    outcome: Schema.Literal("version-conflict"),
    current: KioskPolicyView,
  }),
  Schema.Struct({
    outcome: Schema.Literal("unavailable"),
  })],
);

/**
 * The decoded type of {@link KioskPolicySaveResponse}.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * import type { KioskPolicySaveResponse } from "@ascleo/frontdesk-contracts/kiosk-policy/policy";
 *
 * const result: KioskPolicySaveResponse = { outcome: "unavailable" };
 * ```
 */
export type KioskPolicySaveResponse = typeof KioskPolicySaveResponse.Type;

/**
 * A coarse, safe problem body returned by the SOL administration route.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * import { Schema } from "effect";
 * import { KioskPolicyProblem } from "@ascleo/frontdesk-contracts/kiosk-policy/policy";
 *
 * Schema.decodeUnknownSync(KioskPolicyProblem)({
 *   problem: "malformed-request",
 *   title: "Die Anfrage konnte nicht gelesen werden.",
 * });
 * ```
 */
export const KioskPolicyProblem = Schema.Struct({
  problem: Schema.Literals(
    ["malformed-request",
    "unauthenticated",
    "unavailable",
    "version-conflict"],
  ),
  title: Schema.String,
  current: Schema.optional(KioskPolicyView),
});

/**
 * The decoded type of {@link KioskPolicyProblem}.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * import type { KioskPolicyProblem } from "@ascleo/frontdesk-contracts/kiosk-policy/policy";
 *
 * const problem: KioskPolicyProblem = {
 *   problem: "unavailable",
 *   title: "Die Einstellung ist derzeit nicht verfügbar.",
 * };
 * ```
 */
export type KioskPolicyProblem = typeof KioskPolicyProblem.Type;
